import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import LexicalRichEditor from '../ui/LexicalRichEditorLazy';
import Tooltip from '../ui/Tooltip';
import OsScroll from '../ui/OsScroll';
import CommentMentionPlugin from './CommentMentionPlugin';
import './CommentComposer.css';
import { commentPlainText, commentHasPendingMedia } from '../../extensions/bundled/task-manager/commentRichText';

// Session storage: a draft lives as long as the app is open (Jira keeps its
// comment drafts per issue in the browser the same way). Any storage error —
// a full or blocked store — just means no draft, never a broken composer.
const DRAFT_PREFIX = 'apiboo.commentDraft:';
function readDraft(key) {
  if (!key) return { html: '', picked: [] };
  try {
    const d = JSON.parse(sessionStorage.getItem(DRAFT_PREFIX + key) || 'null');
    return d && typeof d.html === 'string' ? { html: d.html, picked: Array.isArray(d.picked) ? d.picked : [] } : { html: '', picked: [] };
  } catch {
    return { html: '', picked: [] };
  }
}
function writeDraft(key, html, picked) {
  if (!key) return;
  try {
    if (commentPlainText(html).trim() === '' && !/<(img|video)\b/i.test(html)) sessionStorage.removeItem(DRAFT_PREFIX + key);
    else sessionStorage.setItem(DRAFT_PREFIX + key, JSON.stringify({ html, picked }));
  } catch {
    /* no draft this time */
  }
}

/**
 * The task comment composer as a rich editor (card 7de79076, owner
 * 01.10.2026: "instead of basic text a lexical editor there also, so we can
 * add images / videos"). The same LexicalRichEditor and media pipeline as the
 * task description — paste, drop or "/" an image; formatting; lists; code.
 *
 *  - Ctrl/⌘+Enter sends, like the textarea did; Enter makes a new line.
 *  - "@" mentions a teammate on team boards (CommentMentionPlugin).
 *  - Send waits while an image is still uploading: the comment is saved as
 *    HTML, and a still-pending image in it would never get its final URL
 *    (the live rewriter only reaches the open editor, which Send clears).
 *
 *  - Drafts (owner, 06.10.2026, "like Jira"): with a `draftKey`, what you
 *    typed and did not send is kept for this app session — close the task,
 *    open it again, and the comment is still there. Sending clears it.
 *
 * `onSubmit({ html, text, picked })`.
 */
export default function CommentComposer({ media, members, labelOf, mentions = false, onSubmit, onMentionOpenChange, draftKey = null }) {
  const editorRef = useRef(null);
  const [draft] = useState(() => readDraft(draftKey));
  const [html, setHtmlState] = useState(draft.html);
  const pickedRef = useRef(draft.picked);
  const [resetKey, setResetKey] = useState(0);
  const setHtml = useCallback(
    (next) => {
      setHtmlState(next);
      writeDraft(draftKey, next, pickedRef.current);
    },
    [draftKey]
  );

  const text = commentPlainText(html);
  const pending = commentHasPendingMedia(html);
  const hasMedia = /<(img|video)\b/i.test(html);
  const canSend = (text.trim() !== '' || hasMedia) && !pending;

  const send = useCallback(() => {
    if (!canSend) return;
    onSubmit({ html, text: text.trim(), picked: pickedRef.current });
    pickedRef.current = [];
    setHtml(''); // clears the draft too
    setResetKey((k) => k + 1); // a fresh editor: no stale undo history, no media state
  }, [canSend, html, text, onSubmit, setHtml]);

  const plugins = useMemo(
    () =>
      mentions ? (
        <CommentMentionPlugin
          members={members}
          labelOf={labelOf}
          onPicked={(p) => {
            pickedRef.current = [...pickedRef.current, p];
          }}
          onOpenChange={onMentionOpenChange}
        />
      ) : null,
    [mentions, members, labelOf, onMentionOpenChange]
  );

  return (
    <div
      className="task-detail-comment-inputwrap task-detail-comment-rich"
      onKeyDownCapture={(e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          e.stopPropagation();
          send();
        }
      }}
    >
      {/* Long drafts scroll with the app's OverlayScrollbars, not a native bar. */}
      <OsScroll className="task-detail-comment-editor" data-testid="comment-composer">
        <LexicalRichEditor
          key={resetKey}
          ref={editorRef}
          fallback={<div className="task-detail-comment-input">Add a comment…</div>}
          value={resetKey === 0 ? draft.html : ''}
          onChange={setHtml}
          media={media}
          plugins={plugins}
          placeholder="Add a comment… (/ for images, @ to mention)"
          blockHandles={false}
        />
      </OsScroll>
      <Tooltip content={pending ? 'Waiting for the upload…' : 'Send (Ctrl + Enter)'} side="top">
        <button
          type="button"
          className="task-detail-comment-send"
          onClick={send}
          disabled={!canSend}
          aria-label="Send comment"
        >
          <Send size={15} />
        </button>
      </Tooltip>
    </div>
  );
}
